import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileText, Layers, Sparkles, Trophy, MapPin } from "lucide-react";
import { GuideDownloadDialog } from "./GuideDownloadDialog";

const tiers = [
  {
    icon: Layers,
    label: "Tier 1",
    title: "The Foundation",
    desc: "Benefits employees expect — the core of nearly every competitive package.",
    items: [
      {
        name: "Health Insurance",
        detail: "The anchor of any package. North Dakota's group market is primarily served by Blue Cross Blue Shield of North Dakota and Sanford Health Plan. Many employers pair high-deductible plans with tax-advantaged HSAs to manage premiums.",
      },
      {
        name: "Dental & Vision",
        detail: "Inexpensive to add and consistently valued by employees and their families. Often offered as employer subsidized or low cost voluntary options.",
      },
      {
        name: "Retirement Savings",
        detail: "A 401(k) with an employer match is a baseline expectation for many roles. Smaller employers can use simpler, lower-cost structures like a SIMPLE IRA without heavy administration.",
      },
      {
        name: "Life & Disability Coverage",
        detail: "Basic group term life insurance is common, often employer-paid, with options for employees to buy more. Short and long term disability are especially relevant for physically demanding work.",
      },
    ],
  },
  {
    icon: Sparkles,
    label: "Tier 2",
    title: "The Smart Add-Ons",
    desc: "Cost-effective benefits that round out a package and fill the gaps employees feel most.",
    items: [
      {
        name: "Voluntary & Supplemental Benefits",
        detail: "Accident, critical illness, and hospital indemnity coverage. Cost little to nothing for the employer and help employees offset out-of-pocket exposure from high-deductible health plans.",
      },
      {
        name: "HSAs & FSAs",
        detail: "Health Savings Accounts and Flexible Spending Accounts let employees set aside pre-tax dollars for medical costs. Pairs naturally with high-deductible plans, with real tax advantages for both sides.",
      },
      {
        name: "Employee Assistance Programs (EAPs)",
        detail: "Confidential counseling and support services for mental health, financial, and legal matters. Affordable, increasingly expected, and a meaningful signal of employer support for team wellbeing.",
      },
    ],
  },
  {
    icon: Trophy,
    label: "Tier 3",
    title: "The Differentiators",
    desc: "Where employers in a competitive market set themselves apart.",
    items: [
      {
        name: "Generous PTO & Parental Leave",
        detail: "North Dakota does not mandate paid leave, so a strong PTO and parental leave policy is a genuine recruiting advantage. Flexibility here often tips a candidate's decision.",
      },
      {
        name: "Wellness & Mental Health Support",
        detail: "Wellness programs, mental health resources, and preventive-care incentives are increasingly central to how employees evaluate an employer — and can help control long-term health costs.",
      },
      {
        name: "Flexibility & Growth Perks",
        detail: "Flexible or remote work arrangements, tuition assistance, student loan support, and above-market retirement matching are powerful levers for standing out, particularly for skilled and salaried roles.",
      },
    ],
  },
];

const ndRealities = [
  { title: "The energy sector raises the bar", desc: "Activity in the Bakken region tends to lift benefit expectations for skilled and blue-collar roles above comparable states." },
  { title: "Agriculture brings seasonal considerations", desc: "Employers with seasonal workforces face unique decisions around eligibility, coverage, and timing." },
  { title: "Small employers have more options than they think", desc: "Level-funded plans, association health plans, and PEOs can give smaller businesses access to benefits and pricing usually reserved for larger groups." },
  { title: "No state-mandated paid leave", desc: "What you choose to offer is genuinely your decision — and a real opportunity to differentiate." },
];

const TierCard = ({ tier, index }) => (
  <div
    data-testid={`benefits-tier-${index}`}
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all overflow-hidden"
  >
    <div className="bg-[#1A2744] p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[#2EC4C4] text-xs font-bold uppercase tracking-wider">{tier.label}</span>
        <div className="w-10 h-10 bg-[#2EC4C4]/15 border border-[#2EC4C4]/30 rounded-xl flex items-center justify-center">
          <tier.icon size={20} className="text-[#2EC4C4]" />
        </div>
      </div>
      <h3 className="font-bold text-2xl text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{tier.title}</h3>
      <p className="text-gray-300 text-sm leading-relaxed">{tier.desc}</p>
    </div>
    <ul className="p-6 space-y-5">
      {tier.items.map((item) => (
        <li key={item.name}>
          <p className="font-semibold text-[#1A2744] text-base mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.name}</p>
          <p className="text-[#475569] text-sm leading-relaxed">{item.detail}</p>
        </li>
      ))}
    </ul>
  </div>
);

export const BenefitsGuideSection = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
  <section data-testid="benefits-guide-section" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 max-w-3xl mx-auto">
        <div className="inline-flex items-center space-x-2 bg-[#EAF9F9] text-[#2EC4C4] font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-5">
          <FileText size={14} />
          <span>Free Guide</span>
        </div>
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Building a Competitive Benefits Package in North Dakota
        </h2>
        <p className="text-[#475569] text-lg leading-relaxed">
          A strong benefits package is one of the most powerful tools for recruiting and retaining talent. With rising premiums and a tight labor market, the goal is to offer the right things, structured the right way — not everything. Here's how the strongest North Dakota employers think about it.
        </p>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          data-testid="benefits-guide-download-btn"
          className="inline-flex items-center space-x-2 mt-6 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-6 py-3 transition-all shadow-md hover:shadow-lg active:scale-95"
        >
          <Download size={18} />
          <span>Download the Full PDF Guide</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        {tiers.map((tier, i) => <TierCard key={tier.title} tier={tier} index={i} />)}
      </div>

      {/* North Dakota Realities */}
      <div className="bg-[#F8FAFC] rounded-2xl p-8 lg:p-10 border border-gray-100">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-[#EAF9F9] rounded-xl flex items-center justify-center">
            <MapPin size={22} className="text-[#2EC4C4]" />
          </div>
          <div>
            <p className="text-[#2EC4C4] text-xs font-bold uppercase tracking-wider">North Dakota Realities</p>
            <h3 className="font-bold text-2xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              A Few Things Worth Keeping in Mind
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {ndRealities.map((r) => (
            <div key={r.title} className="bg-white rounded-xl p-5 border border-gray-100">
              <p className="font-semibold text-[#1A2744] mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{r.title}</p>
              <p className="text-[#475569] text-sm leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-center text-[#94A3B8] text-xs mt-8 max-w-3xl mx-auto leading-relaxed">
        This guide is for general informational purposes only and does not constitute legal, tax, or financial advice. Benefit options, carriers, regulations, and costs change over time and vary by employer. Please consult Viall Business Solutions or a qualified professional before making decisions about your benefits program.
      </p>
    </div>
    <GuideDownloadDialog open={dialogOpen} onOpenChange={setDialogOpen} />
  </section>
  );
};

/**
 * Compact teaser version for the Home page.
 * Links to the full guide on the Resources page.
 */
export const BenefitsGuideTeaser = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
  <section data-testid="benefits-guide-teaser" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-gradient-to-br from-[#F8FAFC] to-[#EAF9F9] rounded-3xl p-8 lg:p-12 border border-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center space-x-2 bg-white text-[#2EC4C4] font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-5 border border-[#2EC4C4]/20">
              <FileText size={14} />
              <span>Free Guide for ND Employers</span>
            </div>
            <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Building a Competitive Benefits Package in North Dakota
            </h2>
            <p className="text-[#475569] text-base lg:text-lg leading-relaxed mb-6">
              Our 3-tier framework helps you decide what to offer and how to structure it. From foundational health coverage to the differentiators that win top talent — written specifically for North Dakota employers.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/resources"
                data-testid="benefits-guide-read-btn"
                className="inline-flex items-center justify-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-6 py-3 transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                <span>Read the Guide</span><ArrowRight size={18} />
              </Link>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                data-testid="benefits-guide-teaser-download"
                className="inline-flex items-center justify-center space-x-2 bg-white border-2 border-[#1A2744] text-[#1A2744] hover:bg-[#1A2744] hover:text-white font-semibold rounded-lg px-6 py-3 transition-all"
              >
                <Download size={18} /><span>Download PDF</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {tiers.map((tier) => (
              <div key={tier.title} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 bg-[#EAF9F9] rounded-lg flex items-center justify-center mb-3">
                  <tier.icon size={20} className="text-[#2EC4C4]" />
                </div>
                <p className="text-[#2EC4C4] text-xs font-bold uppercase tracking-wider mb-1">{tier.label}</p>
                <p className="font-bold text-base text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>{tier.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <GuideDownloadDialog open={dialogOpen} onOpenChange={setDialogOpen} />
  </section>
  );
};
